import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, HelpCircle, AlertCircle, Loader2 } from 'lucide-react';
import { getSessionId } from '../../lib/sessionManager';
import { StaffingSocket } from '../../lib/staffingSocket';
import { unlockAudio, beep, vibrate } from '../../lib/audioContextManager';
import AslSphere from '../../components/staffing/AslSphere';
import ResultScreen from '../../components/staffing/ResultScreen';
import { getStaffingQuestions } from '../../lib/staffingApi';
import '../../styles/staffing-theme.css';

// Phase 16 — Candidate quick-phrase chip bar. Static preset list; sent over
// the EXISTING candidate socket (no new socket, no localStorage writes).
const QUICK_PHRASES = [
  { label: 'Hello',              pattern: 'wave' },
  { label: 'Looking for a job',  pattern: 'search_work' },
  { label: 'Good morning',       pattern: 'sun_rise' },
  { label: 'Ready to interview', pattern: 'thumbs_up' },
];
const PHRASE_RATE_MS = 800;

export default function CandidateView() {
  // Set document title for Talent Connect routes
  useEffect(() => {
    const prev = document.title;
    document.title = 'Talent Connect — SignVoice';
    return () => { document.title = prev; };
  }, []);

  const [params] = useSearchParams();
  const roomId = params.get('room');
  const [candidateSessionId] = useState(() => getSessionId());
  const [ready, setReady] = useState(false); // user tapped to start (unlocks audio)
  const [wsState, setWsState] = useState('disconnected'); // disconnected|connecting|open|closed
  const [question, setQuestion] = useState(null); // current active question
  const [answered, setAnswered] = useState(false); // already responded the current one
  const [result, setResult] = useState(null);
  const [allAnswers, setAllAnswers] = useState([]);
  const [questionsCatalog, setQuestionsCatalog] = useState([]);
  const [error, setError] = useState('');
  const [flashing, setFlashing] = useState(false);
  const sockRef = useRef(null);

  // Fetch catalogue (for review screen rendering)
  useEffect(() => {
    if (!roomId) return;
    getStaffingQuestions().then(setQuestionsCatalog).catch(() => {});
  }, [roomId]);

  const connect = useCallback(() => {
    if (!roomId) return;
    if (sockRef.current) {
      try { sockRef.current.close(); } catch { /* noop */ }
    }
    const sock = new StaffingSocket(roomId, 'candidate', candidateSessionId);
    sockRef.current = sock;
    setWsState('connecting');

    sock.on('open', () => setWsState('open'));
    sock.on('close', () => setWsState('closed'));
    sock.on('error', () => setWsState('closed'));
    sock.on('error', (msg) => {
      if (msg && msg.message === 'Room not found') setError('Room not found');
    });
    sock.on('room:state', (msg) => {
      if (msg.result) setResult(msg.result);
      if (Array.isArray(msg.answers)) setAllAnswers(msg.answers);
    });
    sock.on('question:asked', (msg) => {
      setQuestion({
        id: msg.question_id,
        text: msg.question_text,
        asl_pattern: msg.asl_pattern || 'idle',
        asl_video_url: msg.asl_video_url || null,
        sequence_num: msg.sequence_num,
      });
      setAnswered(false);
      // Multimodal alert (best-effort)
      vibrate([100, 50, 100]);
      beep({ frequency: 880, durationMs: 110, volume: 0.14 });
      setFlashing(true);
      setTimeout(() => setFlashing(false), 260);
    });
    // Phase 15 (REALTIME_QUESTION_INJECTION, 4/4) — same UX as question:asked
    // but the payload is a free-form recruiter-injected question. The QR
    // is NOT regenerated (same room, same WS).
    sock.on('question_injection', (msg) => {
      setQuestion({
        id: msg.question_id,
        text: msg.question_text,
        asl_pattern: msg.asl_pattern || 'custom',
        asl_video_url: null,
        sequence_num: msg.sequence_num,
        injected: true,
      });
      setAnswered(false);
      vibrate([100, 50, 100]);
      beep({ frequency: 880, durationMs: 110, volume: 0.14 });
      setFlashing(true);
      setTimeout(() => setFlashing(false), 260);
    });
    sock.on('answer:submitted', (msg) => {
      setAllAnswers((prev) => {
        if (prev.some((a) => a.sequence_num === msg.sequence_num)) return prev;
        return [...prev, msg];
      });
    });
    sock.on('session:complete', (msg) => {
      setResult(msg.result);
      setAllAnswers(msg.answers || []);
      setQuestion(null);
    });

    sock.connect();
  }, [roomId, candidateSessionId]);

  const handleReady = useCallback(() => {
    unlockAudio();
    setReady(true);
    connect();
  }, [connect]);

  useEffect(() => {
    return () => {
      try { sockRef.current?.close(); } catch { /* noop */ }
    };
  }, []);

  const submitAnswer = useCallback((ans) => {
    if (!question || answered || result) return;
    const payload = {
      type: 'answer:submitted',
      question_id: question.id,
      answer: ans,
      sequence_num: question.sequence_num,
    };
    if (sockRef.current?.send(payload)) {
      setAnswered(true);
    }
  }, [question, answered, result]);

  // Phase 16 — quick-phrase chip bar state. Refs (not state) for rate-limit
  // + timeouts so hot-firing chips doesn't force extra renders.
  const [phrasePulse, setPhrasePulse] = useState(null); // pattern currently pulsing
  const [phraseToast, setPhraseToast] = useState('');
  const phraseTsRef = useRef({});
  const phrasePulseTimerRef = useRef(null);
  const phraseToastTimerRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(phrasePulseTimerRef.current);
    clearTimeout(phraseToastTimerRef.current);
  }, []);

  const sendPhrase = useCallback((label, pattern) => {
    if (wsState !== 'open' || !sockRef.current) return;   // never throw when disconnected
    const now = Date.now();
    const last = phraseTsRef.current[pattern] || 0;
    if (now - last < PHRASE_RATE_MS) return;              // per-chip rate limit
    phraseTsRef.current[pattern] = now;
    sockRef.current.send({
      type: 'candidate_phrase',
      phrase: label,
      asl_pattern: pattern,
      candidate_session_id: candidateSessionId,
      timestamp: now,
    });
    setPhrasePulse(pattern);
    clearTimeout(phrasePulseTimerRef.current);
    phrasePulseTimerRef.current = setTimeout(() => setPhrasePulse(null), 600);
    setPhraseToast(`Sent: ${label}`);
    clearTimeout(phraseToastTimerRef.current);
    phraseToastTimerRef.current = setTimeout(() => setPhraseToast(''), 1600);
  }, [wsState, candidateSessionId]);

  // ----- render -----
  if (!roomId) {
    return (
      <div className="staffing-theme staffing-candidate" data-testid="candidate-error-room">
        <div className="staffing-bg" aria-hidden="true" />
        <div className="staffing-candidate-error">
          <AlertCircle size={56} />
          <h2>Invalid room</h2>
          <p>This URL is missing the <code>?room=</code> parameter. Scan the recruiter's QR code to join.</p>
          <Link to="/talent-connect" className="staffing-btn staffing-btn--ghost mt-6">Back to Talent Connect</Link>
        </div>
      </div>
    );
  }

  if (error === 'Room not found') {
    return (
      <div className="staffing-theme staffing-candidate" data-testid="candidate-room-not-found">
        <div className="staffing-bg" aria-hidden="true" />
        <div className="staffing-candidate-error">
          <AlertCircle size={56} />
          <h2>Room not found</h2>
          <p>This room may have expired or been closed. Ask the recruiter for a new QR code.</p>
          <Link to="/talent-connect" className="staffing-btn staffing-btn--ghost mt-6">Back to Talent Connect</Link>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="staffing-theme staffing-candidate" data-testid="candidate-ready-gate">
        <div className="staffing-bg" aria-hidden="true" />
        <div className="staffing-ready-screen">
          <AslSphere pattern="idle" size={180} />
          <h2 className="staffing-ready-title">You're entering room</h2>
          <p className="staffing-ready-roomid mono">{roomId}</p>
          <p className="staffing-ready-sub">When you're ready, tap to begin. The recruiter will start asking questions in ASL.</p>
          <button
            type="button"
            onClick={handleReady}
            className="staffing-ready-btn"
            data-testid="candidate-ready-btn"
          >
            I'm Ready
          </button>
          <p className="staffing-disclosure">Demo mode — full ASL animation coming soon</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="staffing-theme staffing-candidate staffing-candidate--result" data-testid="candidate-complete">
        <div className="staffing-bg" aria-hidden="true" />
        <ResultScreen
          result={result}
          role="candidate"
          answers={allAnswers}
          questions={questionsCatalog}
        />
      </div>
    );
  }

  const hasQuestion = !!question && !answered;
  const buttonsDisabled = !hasQuestion;

  return (
    <div className="staffing-theme staffing-candidate" data-testid="candidate-view">
      <div className="staffing-bg" aria-hidden="true" />
      {flashing && <div className="staffing-flash" aria-hidden="true" data-testid="staffing-flash" />}

      <header className="staffing-candidate-header" data-testid="candidate-header">
        <span className="staffing-pill staffing-pill--mini">Room · <code className="mono">{roomId}</code></span>
        <span className={`staffing-pill staffing-pill--mini ${wsState === 'open' ? 'staffing-pill--ok' : 'staffing-pill--warn'}`} data-testid="candidate-ws-pill">
          {wsState === 'open' ? '● Connected' : `● ${wsState}`}
        </span>
      </header>

      {/* TOP HALF — sphere + question */}
      <section className="staffing-candidate-top" data-testid="candidate-top">
        <p className="staffing-question-prompt" data-testid="candidate-question">
          {hasQuestion
            ? question.text
            : answered
              ? 'Answer received. Next question coming…'
              : 'Waiting for question…'}
        </p>
        <AslSphere
          pattern={hasQuestion ? (question.asl_pattern || 'idle') : 'idle'}
          asl_video_url={hasQuestion ? question.asl_video_url : null}
          size={Math.min(window.innerWidth * 0.55, 240)}
          showMessage={!answered}
        />
        <p className="staffing-asl-label">
          {hasQuestion ? <>👋 Signing in ASL · <code className="mono">{question.asl_pattern}</code></> : 'Idle'}
        </p>
        <p className="staffing-disclosure">Demo mode — full ASL animation coming soon</p>
      </section>

      {/* Phase 16 — Candidate quick-phrase chip bar. Sits between the
          question/sphere card and the YES/NO/UNSURE answer row so it
          never overlaps the ASL visual. */}
      <section
        className="staffing-quick-phrases"
        data-testid="candidate-quick-phrases"
        aria-label="Quick phrases"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          justifyContent: 'center',
          padding: '10px 12px 4px',
          position: 'relative',
        }}
      >
        {QUICK_PHRASES.map((p) => {
          const isPulsing = phrasePulse === p.pattern;
          const chipDisabled = wsState !== 'open';
          return (
            <button
              key={p.pattern}
              type="button"
              onClick={() => sendPhrase(p.label, p.pattern)}
              disabled={chipDisabled}
              title={chipDisabled ? 'Waiting for recruiter…' : `Send: ${p.label}`}
              aria-label={`Send phrase: ${p.label}`}
              data-testid={`quick-phrase-${p.pattern}`}
              data-pulsing={isPulsing ? 'true' : 'false'}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                color: '#F8FAFC',
                background: isPulsing ? '#7F00FF' : 'rgba(127, 0, 255, 0.14)',
                border: '1px solid rgba(127, 0, 255, 0.4)',
                cursor: chipDisabled ? 'not-allowed' : 'pointer',
                opacity: chipDisabled ? 0.4 : 1,
                transition: 'background 200ms ease, transform 180ms ease, box-shadow 200ms ease',
                transform: isPulsing ? 'scale(1.06)' : 'scale(1)',
                boxShadow: isPulsing ? '0 0 12px rgba(127, 0, 255, 0.55)' : 'none',
              }}
            >
              {p.label}
            </button>
          );
        })}
        {phraseToast && (
          <span
            role="status"
            aria-live="polite"
            data-testid="quick-phrase-toast"
            style={{
              width: '100%',
              textAlign: 'center',
              fontSize: 12,
              color: '#7F00FF',
              marginTop: 2,
            }}
          >
            {phraseToast}
          </span>
        )}
      </section>

      {/* BOTTOM HALF — 3 buttons */}
      <section className="staffing-candidate-bottom" data-testid="candidate-bottom">
        <button
          type="button"
          onClick={() => submitAnswer('yes')}
          disabled={buttonsDisabled}
          className="staffing-answer-btn staffing-answer-btn--yes"
          data-testid="answer-yes"
          aria-label="Yes"
        >
          <ThumbsUp size={32} strokeWidth={2.4} />
          <span>YES</span>
        </button>
        <button
          type="button"
          onClick={() => submitAnswer('no')}
          disabled={buttonsDisabled}
          className="staffing-answer-btn staffing-answer-btn--no"
          data-testid="answer-no"
          aria-label="No"
        >
          <ThumbsDown size={32} strokeWidth={2.4} />
          <span>NO</span>
        </button>
        <button
          type="button"
          onClick={() => submitAnswer('unsure')}
          disabled={buttonsDisabled}
          className="staffing-answer-btn staffing-answer-btn--unsure"
          data-testid="answer-unsure"
          aria-label="Unsure"
        >
          <HelpCircle size={32} strokeWidth={2.4} />
          <span>UNSURE</span>
        </button>
      </section>
    </div>
  );
}
